// Glass / iOS-inspired design system for APIT platform — Mantine v6
// Single source of truth for all glass tokens and component overrides.

// ─── Design Tokens ────────────────────────────────────────────────────────────

const BLUR = {
	heavy: "blur(28px)",
	popup: "blur(20px)",
	panel: "blur(14px)",
	light: "blur(8px)",
};

const SURFACE = {
	dark: {
		base:     "rgba(20, 23, 34, 0.82)",
		elevated: "rgba(26, 30, 44, 0.88)",
		popup:    "rgba(22, 25, 38, 0.93)",
		input:    "rgba(255, 255, 255, 0.05)",
		header:   "rgba(15, 18, 28, 0.90)",
	},
	light: {
		base:     "rgba(255, 255, 255, 0.72)",
		elevated: "rgba(255, 255, 255, 0.85)",
		popup:    "rgba(255, 255, 255, 0.92)",
		input:    "rgba(255, 255, 255, 0.90)",
		header:   "rgba(255, 255, 255, 0.88)",
	},
};

const BORDER = {
	dark:  "rgba(255, 255, 255, 0.08)",
	light: "rgba(0, 0, 0, 0.08)",
};

const SHADOW = {
	dark: {
		xs:    "0 1px 4px rgba(0,0,0,0.22)",
		sm:    "0 2px 8px rgba(0,0,0,0.30), 0 0 0 1px rgba(255,255,255,0.04)",
		md:    "0 4px 16px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.05)",
		lg:    "0 8px 28px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.06)",
		xl:    "0 14px 44px rgba(0,0,0,0.60), 0 0 0 1px rgba(255,255,255,0.07)",
		popup: "0 20px 60px rgba(0,0,0,0.68), 0 0 0 1px rgba(255,255,255,0.09)",
	},
	light: {
		xs:    "0 1px 4px rgba(0,0,0,0.06)",
		sm:    "0 2px 8px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)",
		md:    "0 4px 16px rgba(0,0,0,0.09), 0 0 0 1px rgba(0,0,0,0.04)",
		lg:    "0 8px 28px rgba(0,0,0,0.11), 0 0 0 1px rgba(0,0,0,0.05)",
		xl:    "0 14px 44px rgba(0,0,0,0.13), 0 0 0 1px rgba(0,0,0,0.05)",
		popup: "0 20px 60px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.06)",
	},
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function surf(theme, level = "base") {
	return SURFACE[theme.colorScheme === "dark" ? "dark" : "light"][level];
}

function bdr(theme) {
	return theme.colorScheme === "dark" ? BORDER.dark : BORDER.light;
}

function shd(theme, level = "md") {
	const map = SHADOW[theme.colorScheme === "dark" ? "dark" : "light"];
	return map[level] ?? map.md;
}

function glassBox(theme, surfaceLevel = "popup", blurKey = "popup") {
	return {
		background:          surf(theme, surfaceLevel),
		backdropFilter:      BLUR[blurKey] ?? BLUR.popup,
		WebkitBackdropFilter: BLUR[blurKey] ?? BLUR.popup,
		border:              `1px solid ${bdr(theme)}`,
		boxShadow:           shd(theme, surfaceLevel === "popup" ? "popup" : surfaceLevel === "elevated" ? "lg" : "md"),
	};
}

function inputBase(theme) {
	return {
		background:          surf(theme, "input"),
		backdropFilter:      BLUR.light,
		WebkitBackdropFilter: BLUR.light,
		border:              `1px solid ${bdr(theme)}`,
		"&:focus": {
			border:     `1px solid ${theme.fn.rgba(theme.colors.cyan[5], 0.65)}`,
			boxShadow:  `0 0 0 3px ${theme.fn.rgba(theme.colors.cyan[5], 0.12)}`,
		},
		"&::placeholder": { opacity: 0.45 },
	};
}

function focusWithin(theme) {
	return {
		"&:focus-within": {
			border:    `1px solid ${theme.fn.rgba(theme.colors.cyan[5], 0.65)}`,
			boxShadow: `0 0 0 3px ${theme.fn.rgba(theme.colors.cyan[5], 0.12)}`,
		},
	};
}

function dropdownItemStyles(theme) {
	const isDark = theme.colorScheme === "dark";
	const selectedBg = isDark
		? theme.fn.rgba(theme.colors.cyan[7], 0.58)
		: theme.fn.rgba(theme.colors.cyan[5], 0.42);
	const selectedHoverBg = isDark
		? theme.fn.rgba(theme.colors.cyan[6], 0.68)
		: theme.fn.rgba(theme.colors.cyan[6], 0.52);
	const selectedColor = isDark ? theme.white : theme.colors.cyan[9];
	const hoverBg = isDark
		? theme.fn.rgba(theme.colors.cyan[8], 0.32)
		: theme.fn.rgba(theme.colors.cyan[4], 0.28);

	return {
		borderRadius: theme.radius.sm,
		"&[data-hovered]": {
			background: hoverBg,
		},
		"&[data-selected]": {
			background: selectedBg,
			color: selectedColor,
			fontWeight: 500,
		},
		"&[data-selected][data-hovered]": {
			background: selectedHoverBg,
			color: selectedColor,
		},
	};
}

// ─── Component Overrides ──────────────────────────────────────────────────────

const components = {

	// ── Layout ─────────────────────────────────────────────────────────────────

	Anchor: {
		defaultProps: { underline: false },
		styles: { root: { textDecoration: "none", color: "inherit" } },
	},

	// ── Overlays ───────────────────────────────────────────────────────────────

	Modal: {
		defaultProps: {
			radius: "lg",
			overlayProps: { opacity: 0.50, blur: 5 },
		},
		styles: (theme) => ({
			content: {
				borderRadius: theme.radius.lg,
				border:       `1px solid ${bdr(theme)}`,
				boxShadow:    shd(theme, "popup"),
			},
			header: {
				borderBottom:  `1px solid ${bdr(theme)}`,
				paddingBottom: 12,
			},
		}),
	},

	Drawer: {
		defaultProps: {
			overlayProps: { opacity: 0.40, blur: 8 },
		},
		styles: (theme) => ({
			content: {
				background:          surf(theme, "popup"),
				backdropFilter:      BLUR.heavy,
				WebkitBackdropFilter: BLUR.heavy,
				border:              `1px solid ${bdr(theme)}`,
				boxShadow:           shd(theme, "popup"),
			},
			header: {
				background:   "transparent",
				borderBottom: `1px solid ${bdr(theme)}`,
			},
		}),
	},

	// ── Surfaces ───────────────────────────────────────────────────────────────

	Paper: {
		defaultProps: { radius: "lg" },
		styles: (theme) => ({
			root: {
				background:           surf(theme, "elevated"),
				backdropFilter:       BLUR.panel,
				WebkitBackdropFilter: BLUR.panel,
			},
		}),
	},

	Card: {
		defaultProps: { radius: "lg" },
		styles: (theme) => ({
			root: {
				background:           surf(theme, "elevated"),
				backdropFilter:       BLUR.panel,
				WebkitBackdropFilter: BLUR.panel,
			},
		}),
	},

	// ── Dropdowns / Floating ───────────────────────────────────────────────────

	Menu: {
		styles: (theme) => ({
			dropdown: {
				...glassBox(theme, "popup"),
				borderRadius: theme.radius.md,
				padding: "4px",
			},
			item: {
				borderRadius: theme.radius.sm,
			},
			label: {
				opacity: 0.55,
				fontSize: 11,
				fontWeight: 700,
				textTransform: "uppercase",
				letterSpacing: "0.05em",
			},
			divider: {
				borderColor: bdr(theme),
			},
		}),
	},

	Popover: {
		styles: (theme) => ({
			dropdown: {
				...glassBox(theme, "popup"),
				borderRadius: theme.radius.md,
			},
			arrow: {
				background:  surf(theme, "popup"),
				borderColor: bdr(theme),
			},
		}),
	},

	Tooltip: {
		defaultProps: { radius: "sm", withArrow: true },
		styles: (theme) => ({
			tooltip: {
				background: theme.colorScheme === "dark"
					? "rgba(20, 24, 38, 0.96)"
					: "rgba(28, 33, 50, 0.90)",
				color:               theme.colorScheme === "dark" ? theme.colors.gray[1] : theme.white,
				backdropFilter:      BLUR.light,
				WebkitBackdropFilter: BLUR.light,
				border:              `1px solid ${bdr(theme)}`,
				boxShadow:           shd(theme, "sm"),
				fontSize:            12,
				fontWeight:          500,
			},
		}),
	},

	// ── Inputs ─────────────────────────────────────────────────────────────────

	TextInput: {
		defaultProps: { radius: "md" },
		styles: (theme) => ({ input: inputBase(theme) }),
	},

	PasswordInput: {
		defaultProps: { radius: "md" },
		styles: (theme) => ({
			input: {
				background:          surf(theme, "input"),
				backdropFilter:      BLUR.light,
				WebkitBackdropFilter: BLUR.light,
				border:              `1px solid ${bdr(theme)}`,
				...focusWithin(theme),
			},
		}),
	},

	NumberInput: {
		defaultProps: { radius: "md" },
		styles: (theme) => ({ input: inputBase(theme) }),
	},

	Textarea: {
		defaultProps: { radius: "md" },
		styles: (theme) => ({ input: inputBase(theme) }),
	},

	NativeSelect: {
		defaultProps: { radius: "md" },
		styles: (theme) => ({ input: inputBase(theme) }),
	},

	Select: {
		defaultProps: { radius: "md" },
		styles: (theme) => ({
			input: inputBase(theme),
			dropdown: {
				...glassBox(theme, "popup"),
				borderRadius: theme.radius.md,
				padding: "4px",
			},
			item: dropdownItemStyles(theme),
		}),
	},

	MultiSelect: {
		defaultProps: { radius: "md" },
		styles: (theme) => ({
			input: {
				background:          surf(theme, "input"),
				backdropFilter:      BLUR.light,
				WebkitBackdropFilter: BLUR.light,
				border:              `1px solid ${bdr(theme)}`,
				...focusWithin(theme),
			},
			dropdown: {
				...glassBox(theme, "popup"),
				borderRadius: theme.radius.md,
				padding: "4px",
			},
			item: dropdownItemStyles(theme),
			value: {
				background:   theme.colorScheme === "dark"
					? theme.fn.rgba(theme.colors.cyan[9], 0.30)
					: theme.fn.rgba(theme.colors.cyan[1], 0.60),
				border:       `1px solid ${theme.fn.rgba(theme.colors.cyan[5], 0.22)}`,
				borderRadius: theme.radius.xs,
			},
		}),
	},

	// ── Buttons / Actions ──────────────────────────────────────────────────────

	Button: {
		defaultProps: {},
	},

	ActionIcon: {
		defaultProps: {},
	},

	// ── Navigation ─────────────────────────────────────────────────────────────

	NavLink: {
		styles: (theme) => ({
			root: {
				borderRadius: theme.radius.md,
				"&[data-active]": {
					background: theme.colorScheme === "dark"
						? `linear-gradient(135deg, ${theme.fn.rgba(theme.colors.cyan[9], 0.30)} 0%, ${theme.fn.rgba(theme.colors.teal[9], 0.20)} 100%)`
						: `linear-gradient(135deg, ${theme.fn.rgba(theme.colors.cyan[1], 0.80)} 0%, ${theme.fn.rgba(theme.colors.teal[0], 0.60)} 100%)`,
					color: theme.colorScheme === "dark" ? theme.colors.cyan[3] : theme.colors.cyan[7],
				},
				"&:hover:not([data-active])": {
					background: theme.colorScheme === "dark"
						? "rgba(255,255,255,0.05)"
						: "rgba(0,0,0,0.04)",
				},
			},
		}),
	},

	Tabs: {
		styles: (theme) => ({
			tab: {
				fontWeight: 500,
				borderRadius: `${theme.radius.sm}px ${theme.radius.sm}px 0 0`,
				"&[data-active]": {
					borderColor: theme.colors.cyan[6],
					color: theme.colorScheme === "dark" ? theme.colors.cyan[3] : theme.colors.cyan[7],
				},
				"&:hover": {
					background: theme.colorScheme === "dark"
						? "rgba(255,255,255,0.05)"
						: "rgba(0,0,0,0.03)",
				},
			},
			tabsList: {
				borderBottom: "none",
			},
			panel: {
				padding: 0,
			},
		}),
	},

	Accordion: {
		styles: (theme) => ({
			control: {
				borderRadius: theme.radius.md,
				"&:hover": {
					background: theme.colorScheme === "dark"
						? "rgba(255,255,255,0.05)"
						: "rgba(0,0,0,0.03)",
				},
			},
			item: {
				borderColor: bdr(theme),
				borderRadius: theme.radius.md,
				overflow: "hidden",
				"&[data-active]": {},
			},
		}),
	},

	// ── Feedback ───────────────────────────────────────────────────────────────

	Badge: {
		defaultProps: { radius: "sm" },
		styles: {
			root: {
				fontWeight: 600,
				textTransform: "uppercase",
				letterSpacing: "0.02em",
			},
		},
	},

	Notification: {
		defaultProps: { radius: "lg" },
		styles: (theme) => ({
			root: {
				...glassBox(theme, "elevated", "panel"),
				borderRadius: theme.radius.lg,
				boxShadow:    shd(theme, "lg"),
			},
		}),
	},
};

// ─── Theme Export ─────────────────────────────────────────────────────────────

export const GLASS_TOKENS = { BLUR, SURFACE, BORDER, SHADOW };

// surf / bdr / shd exported so main.jsx inline styles can reuse the same tokens
export { surf as glassSurface, bdr as glassBorder, shd as glassShadow };

export const glassTheme = {
	// Soft layered shadows — light-mode values; dark override happens per-component
	shadows: {
		xs: "0 1px 3px rgba(0,0,0,0.08)",
		sm: "0 2px 6px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)",
		md: "0 4px 12px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.06)",
		lg: "0 8px 24px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.05)",
		xl: "0 16px 40px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.04)",
	},

	// Radius scale: consistent glass-feel sizing
	radius: {
		xs: 4,
		sm: 8,
		md: 12,
		lg: 16,
		xl: 20,
	},

	defaultRadius: "md",
	primaryColor: "cyan",

	fontFamily: "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
	headings: {
		fontFamily: "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
		fontWeight: 700,
	},

	components,
};
